function normalizeNumber(value) {
	const normalized = String(value || '').trim();
	return /^\d{1,4}$/.test(normalized) ? normalized : null;
}

function getPageUrl(number) {
	return `/numbers/${number}/`;
}

async function loadReading(number, signal) {
	const response = await fetch(getPageUrl(number), {
		headers: { 'X-Requested-With': 'fetch' },
		signal,
	});
	if (!response.ok) throw new Error(`Number ${number} is not available in the archive.`);

	const html = await response.text();
	const documentFragment = new DOMParser().parseFromString(html, 'text/html');
	const reading = documentFragment.querySelector('[data-reading-number]');
	if (!reading || !reading.dataset.readingSummaryText)
		throw new Error(`Number ${number} does not have comparison data.`);

	const themes = [...documentFragment.querySelectorAll('[data-reading-theme]')].map((theme) => ({
		id: theme.dataset.themeId,
		label: theme.dataset.themeLabel,
		evidence: theme.querySelector('[data-theme-evidence]')?.textContent.trim() || '',
	}));
	return {
		number,
		url: getPageUrl(number),
		sourceType: reading.dataset.readingSourceType || 'sourced',
		provenance: reading.dataset.readingProvenanceLabel || 'Source information unavailable',
		summary: reading.dataset.readingSummaryText,
		themes,
	};
}

function createElement(tag, className, text) {
	const element = document.createElement(tag);
	if (className) element.className = className;
	if (typeof text === 'string') element.textContent = text;
	return element;
}

function renderThemeList(themes) {
	const list = createElement('dl', 'compare-reading__themes');
	for (const theme of themes) {
		const term = createElement('dt', null, theme.label);
		const description = createElement('dd', null, theme.evidence);
		list.append(term, description);
	}
	return list;
}

function renderReading(reading, sharedThemeIds) {
	const article = createElement('article', 'compare-reading');
	const header = createElement('header', 'compare-reading__header');
	const title = createElement('h2');
	const link = createElement('a', null, `Angel Number ${reading.number}`);
	link.href = reading.url;
	title.append(link);
	const provenance = createElement('p', 'compare-reading__provenance', reading.provenance);
	header.append(title, provenance);

	const quote = createElement('blockquote');
	quote.append(createElement('p', null, reading.summary));

	const distinctThemes = reading.themes.filter((theme) => !sharedThemeIds.has(theme.id));
	article.append(header, quote);
	if (distinctThemes.length) {
		article.append(createElement('h3', null, 'Themes distinct in this entry'));
		article.append(renderThemeList(distinctThemes));
	}
	return article;
}

function renderComparison(readings, root) {
	root.innerHTML = '';
	const [first, second] = readings;
	const secondThemeIds = new Set(second.themes.map((theme) => theme.id));
	const sharedThemes = first.themes.filter((theme) => secondThemeIds.has(theme.id));
	const sharedThemeIds = new Set(sharedThemes.map((theme) => theme.id));

	const overlap = createElement('section', 'compare-overlap');
	overlap.append(createElement('h2', null, 'Shared source themes'));
	if (sharedThemes.length) {
		const list = createElement('div', 'compare-overlap__list');
		for (const theme of sharedThemes) {
			const secondTheme = second.themes.find((candidate) => candidate.id === theme.id);
			const item = createElement('section', 'compare-overlap__theme');
			item.append(createElement('h3', null, theme.label));
			const evidence = createElement('div', 'compare-overlap__evidence');
			for (const [reading, supportedTheme] of [
				[first, theme],
				[second, secondTheme],
			]) {
				const quote = createElement('blockquote');
				quote.append(createElement('p', null, supportedTheme.evidence));
				quote.append(createElement('cite', null, `Angel Number ${reading.number}`));
				evidence.append(quote);
			}
			item.append(evidence);
			list.append(item);
		}
		overlap.append(list);
	} else {
		overlap.append(createElement('p', 'compare-overlap__empty', 'No directly matched source themes overlap.'));
	}

	const columns = createElement('div', 'compare-results__columns');
	columns.append(renderReading(first, sharedThemeIds), renderReading(second, sharedThemeIds));
	root.append(overlap, columns);
}

export default function numberComparison() {
	const root = document.querySelector('[data-number-comparison]');
	if (!root || root.dataset.comparisonInitialized) return;
	root.dataset.comparisonInitialized = 'true';

	const form = root.querySelector('[data-comparison-form]');
	const firstInput = root.querySelector('[data-comparison-first]');
	const secondInput = root.querySelector('[data-comparison-second]');
	const status = root.querySelector('[data-comparison-status]');
	const results = root.querySelector('[data-comparison-results]');
	let activeRequest = null;

	const compare = async ({ updateUrl = true } = {}) => {
		const first = normalizeNumber(firstInput.value);
		const second = normalizeNumber(secondInput.value);
		if (!first || !second) {
			status.textContent = 'Enter two available numbers containing one to four digits.';
			results.innerHTML = '';
			return;
		}
		if (first === second) {
			status.textContent = 'Choose two different numbers to compare.';
			results.innerHTML = '';
			return;
		}

		activeRequest?.abort();
		activeRequest = new AbortController();
		status.textContent = `Comparing ${first} and ${second}...`;
		results.setAttribute('aria-busy', 'true');
		try {
			const readings = await Promise.all([
				loadReading(first, activeRequest.signal),
				loadReading(second, activeRequest.signal),
			]);
			renderComparison(readings, results);
			status.textContent = `Showing source-grounded differences for ${first} and ${second}.`;
			if (updateUrl) {
				const url = new URL(window.location.href);
				url.searchParams.set('numbers', `${first},${second}`);
				window.history.replaceState({}, '', url);
			}
		} catch (error) {
			if (error.name !== 'AbortError') {
				status.textContent = error.message || 'The comparison could not be loaded.';
				results.innerHTML = '';
			}
		} finally {
			results.removeAttribute('aria-busy');
		}
	};

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		void compare();
	});

	const requested = new URL(window.location.href).searchParams.get('numbers')?.split(',') || [];
	if (requested.length === 2) {
		firstInput.value = requested[0];
		secondInput.value = requested[1];
		void compare({ updateUrl: false });
	}
}
